import { ShiftPlannerBranchProfile } from 'src/app/core/data/company-type/company-type-registry';

/**
 * C5 Schritt 3 — Branchensprache im Kundenkonto.
 *
 * ## Der Befund, der das nötig macht
 *
 * Die **Vorführung** ist branchenspezifisch, der **Alltag** nicht — genau falsch herum. In
 * der Demo liefert `demoSuggestions(industry)` Sätze in der Sprache der Branche; im
 * Kundenkonto rotieren zwei feste Pools, und ein Pflegedienst mit 24 Personen bekommt
 * dieselben sechs Sätze wie ein Lieferbetrieb mit 200.
 *
 * ## Woher die Branche kommt
 *
 * `CompanyDetails.CompanyTypeId` → `getShiftPlannerProfileFor()` aus
 * `core/data/company-type/company-type-registry.ts`. Die Registry führt 18 Firmenarten und
 * ist mit dem Backend abgeglichen — **eine** Quelle, kein zweiter Katalog. Jede neue
 * Firmenart erbt automatisch das passende Profil.
 *
 * ## Warum fünf Listen und nicht neun
 *
 * Die Registry kennt neun Planerprofile. Eine eigene Satzliste lohnt nur, wo die Branche
 * eine eigene **Sprache** hat: Pflege sagt „Dienst", Gastro „Küche/Service", Logistik
 * „Tour/Fahrer", Handel „Kasse". Büro, Callcenter, Produktion und Sicherheit sagen
 * schlicht „Schicht" — für sie wäre eine eigene Liste dieselbe Liste unter anderem Namen
 * und eine weitere Stelle, die driftet. Sie fallen auf `Generic`.
 *
 * ---
 *
 * ## ⚠️ Die zwei Regeln, ohne die diese Datei Schaden anrichtet
 *
 * **1 · Verb aus dem Katalogwortschatz, Branchenfarbe im Substantiv.** Am 04.09.2026 waren
 * 15 von 30 Demo-Sätzen tot: neun landeten in der Hilfe-Rücklage, sechs lösten gar keine
 * Aktion aus. Sie waren als Verkaufstext geschrieben. Der Unterschied ist haarfein — „Gib
 * die Schichten **an der Kasse** frei" fällt durch, „Gib die **Kassen-Schichten** frei"
 * trägt. Ein toter Vorschlag ist schlimmer als ein generischer.
 *
 * **2 · Keine erfundenen Abteilungen.** Die Demo-Sätze nennen Demo-Abteilungen
 * („Ambulanter Dienst", „Service"); im Kundenkonto gibt es die nicht, und der
 * `IEntityResolver` findet sie nicht. Sätze mit Ortsbezug tragen deshalb `{{dept}}` und
 * bekommen einen **echten** Abteilungsnamen aus `DepartmentService.departementList`
 * eingesetzt — dieselbe Mechanik, mit der `{{name}}` seit jeher einen echten Mitarbeiter
 * bekommt. Sätze ohne `{{dept}}` funktionieren in jedem Betrieb.
 *
 * **Nicht raten, messen:** `_local-analysis/misc/demo-assistent-vorschlaege-pruefen.py`
 * schickt jeden Satz mit frischer Unterhaltung an `/api/Chatbot/Send`, liest die
 * Werkzeugwahl aus Seq und schreibt einen Bericht. Es schreibt nichts — Write-Werkzeuge
 * liefern nur einen Vorschlag, `Confirm` wird nie gerufen.
 */

/** Schlüssel relativ zu `HELP_CHAT.SUGGESTED.` — dasselbe Präfix wie die Demo-Sätze. */
export interface ProfileSuggestionSet {
  /** „Erfahren" — was das System weiss. */
  ask: readonly string[];
  /** „Erledigen" — was es auf Zuruf tut. */
  do: readonly string[];
  /** true, wenn mindestens ein Satz `{{dept}}` trägt und damit eine Abteilung braucht. */
  needsDepartment: boolean;
}

/**
 * Die fünf Sprachfarben.
 *
 * Jeder Satz ist an einem `ExampleUtterance` des Zielwerkzeugs ausgerichtet; das Werkzeug
 * steht im Kommentar. Wer hier etwas ändert, misst es nach.
 */
const PROFILE_SUGGESTIONS: Partial<Record<ShiftPlannerBranchProfile, ProfileSuggestionSet>> = {
  // „Dienst" statt „Schicht"; Ruhezeiten und Fairness sind in der Pflege die Dauerthemen.
  Healthcare: {
    ask: [
      'PROF_CARE_ASK_REST', // rest_period_check
      'PROF_CARE_ASK_FAIRNESS', // shift_fairness   (= DEMO_PFLEGE_ASK_3, 30/30 gemessen)
    ],
    do: [
      'PROF_CARE_DO_REPLACEMENT', // suggest_shift_replacement  {{dept}}
      'PROF_CARE_DO_AUTOFILL', // auto_fill_shifts (= DEMO_PFLEGE_DO_1, gemessen)
    ],
    needsDepartment: true,
  },
  // Küche/Service, Wochenendbetrieb.
  Gastronomy: {
    ask: [
      'PROF_GASTRO_ASK_COVERAGE', // shift_coverage             {{dept}}
      'PROF_GASTRO_ASK_WEEKEND', // who_works
    ],
    do: [
      'PROF_GASTRO_DO_REPLACEMENT', // suggest_shift_replacement  {{dept}}
      'PROF_GASTRO_DO_PUBLISH', // publish_shifts
    ],
    needsDepartment: true,
  },
  // Kasse/Verkauf, Samstagsgeschäft.
  Retail: {
    ask: [
      'PROF_RETAIL_ASK_SATURDAY', // who_works                  {{dept}}
      'PROF_RETAIL_ASK_COVERAGE', // shift_coverage             {{dept}}
    ],
    do: [
      'PROF_RETAIL_DO_AUTOFILL', // auto_fill_shifts
      'PROF_RETAIL_DO_PUBLISH', // publish_shifts
    ],
    needsDepartment: true,
  },
  // Fahrer/Touren.
  Transportation: {
    ask: [
      'PROF_LOGISTICS_ASK_TOMORROW', // who_works
      'PROF_LOGISTICS_ASK_UNPLANNED', // find_unscheduled_employees (= DEMO_LOGISTIK_ASK_3)
    ],
    do: [
      'PROF_LOGISTICS_DO_REPLACEMENT', // suggest_shift_replacement (exakter Katalogsatz)
      'PROF_LOGISTICS_DO_AUTOFILL', // auto_fill_shifts (= DEMO_LOGISTIK_DO_2, gemessen)
    ],
    needsDepartment: false,
  },
};

/**
 * Satzliste für ein Planerprofil — oder `null`, wenn dieses Profil keine eigene
 * Sprachfarbe hat.
 *
 * `null` und nicht etwa eine Ersatzliste: Der Aufrufer soll dann die **bestehenden**
 * allgemeinen Pools nehmen, die seit Langem im Einsatz und gemessen sind. Eine dritte,
 * halbgare Liste dazwischen wäre die Drift, vor der der Kopf dieser Datei warnt.
 */
export function profileSuggestions(
  profile: ShiftPlannerBranchProfile,
): ProfileSuggestionSet | null {
  return PROFILE_SUGGESTIONS[profile] ?? null;
}
